import type { FieldConfigurationDto } from '@tempo-io/fim3-dto';
import { Button } from '@tempo-io/sui-button';
import { Calendar } from '@tempo-io/sui-calendar';
import { cn } from '@tempo-io/sui-class-utils';
import { CalendarToday } from '@tempo-io/sui-icons';
import { Popover, PopoverContent, PopoverTrigger } from '@tempo-io/sui-popover';
import { format } from 'date-fns/format';
import { useState } from 'react';
import { GRID_DATE_PICKER_POPPER_CLASS } from './datePickerConstants';

export const parseCalendarDate = (isoCalendarDate: string): Date => {
  const [year, month, day] = isoCalendarDate.split('-').map(Number);
  return new Date(year as number, (month as number) - 1, day as number);
};

export const customDatetoIsoDateString = (date: Date | undefined | null): string | undefined => {
  if (!date) {
    return undefined;
  }
  return format(date, 'yyyy-MM-dd');
};

export const DateFieldInput = ({
  fieldConfiguration,
  values,
  setValues,
  autoFocus,
  isEditableText,
}: {
  fieldConfiguration: FieldConfigurationDto;
  values: string[];
  setValues: (values: string[]) => void;
  isEditableText?: boolean;
  autoFocus?: boolean;
}) => {
  const [open, setOpen] = useState(autoFocus ?? false);

  if (fieldConfiguration.fieldType !== 'DATE') return null;

  const selectedDate = values[0] ? parseCalendarDate(values[0]) : undefined;
  const displayValue = selectedDate ? format(selectedDate, 'MM/dd/yyyy') : '';

  const handleSelect = (date: Date | undefined) => {
    setValues([customDatetoIsoDateString(date) ?? '']);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={isEditableText ? 'ghost' : 'outline'}
          className={cn(
            'fs-exclude w-full justify-start font-normal',
            isEditableText &&
              cn(
                'group border border-transparent hover:border-border-neutral hover:shadow-xs',
                open && 'border-border-focus-neutral ring-[3px] ring-border-focus-neutral/40',
              ),
          )}
          data-testid="fim3-custom-field-input-date"
          data-dd-action-name="FIM 3 Custom Field - date picker redacted"
        >
          <CalendarToday />
          {displayValue || (
            <span className={isEditableText ? 'invisible group-hover:visible' : undefined}>
              Select date
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={GRID_DATE_PICKER_POPPER_CLASS}>
        <Calendar
          mode="single"
          selected={selectedDate}
          defaultMonth={selectedDate}
          onSelect={handleSelect}
        />
      </PopoverContent>
    </Popover>
  );
};
